import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Search, LogOut, ArrowLeft, FileText, Save, Plus, ExternalLink, Download, CalendarClock, Mail, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { adminApi, adminToken, API } from "@/lib/api";
import { T } from "@/lib/testIds";
import { statusIconFor } from "@/components/BrandIcons";

const STATUSES = [
  "Application Received", "Under Review", "Interview",
  "Additional Information Required", "Approved", "Not Selected"
];

const STATUS_COLOR = {
  "Application Received": "bg-blue-100 text-blue-800",
  "Under Review": "bg-yellow-100 text-yellow-800",
  "Interview": "bg-indigo-100 text-indigo-800",
  "Additional Information Required": "bg-orange-100 text-orange-800",
  "Approved": "bg-emerald-100 text-emerald-800",
  "Not Selected": "bg-red-100 text-red-800",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const name = adminToken.name();

  const load = useCallback(async () => {
    if (!adminToken.get()) { navigate("/admin/login"); return; }
    try {
      setLoading(true);
      const { data } = await adminApi.get("/admin/applications", { params: { q: q || undefined, status } });
      setItems(data.items);
    } catch (err) {
      if (err.response?.status === 401) { adminToken.clear(); navigate("/admin/login"); return; }
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  }, [q, status, navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = () => { adminToken.clear(); navigate("/admin/login"); };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/applications.csv", { params: { q: q || undefined, status }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `moreno-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const stats = useMemo(() => {
    const s = { total: items.length };
    STATUSES.forEach((st) => { s[st] = items.filter((i) => i.status === st).length; });
    return s;
  }, [items]);

  if (selected) return <ApplicationDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-up">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-2">Admin Dashboard</div>
          <h1 data-testid={T.admin.welcome} className="text-3xl sm:text-4xl font-bold text-[#0A192F]">Welcome, {name}</h1>
          <p className="mt-1 text-slate-600">Manage driver applications, statuses, and notes.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-semibold">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={exportCsv} data-testid="admin-export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={logout} data-testid={T.admin.logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A192F] hover:bg-[#12244a] text-white text-sm font-semibold">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats.total} accent="#0A192F" />
        <StatCard label="Under Review" value={stats["Under Review"]} accent="#F59E0B" />
        <StatCard label="Interview" value={stats["Interview"]} accent="#6366F1" />
        <StatCard label="Approved" value={stats["Approved"]} accent="#10B981" />
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid={T.admin.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, ID, phone…"
            className="w-full rounded-full border border-slate-200 pl-11 pr-4 py-2.5 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
          />
        </div>
        <select data-testid={T.admin.statusFilter} value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm bg-white">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div data-testid={T.admin.table} className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Application ID</th>
                <th className="text-left px-5 py-3 font-semibold">Applicant</th>
                <th className="text-left px-5 py-3 font-semibold">Contact</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Submitted</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-10 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">No applications yet.</td></tr>}
              {items.map((it) => (
                <tr key={it.application_id} data-testid={T.admin.row(it.application_id)} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono font-semibold text-[#0A192F]">{it.application_id}</td>
                  <td className="px-5 py-3">{it.first_name} {it.last_name}</td>
                  <td className="px-5 py-3 text-slate-600 text-xs">
                    <div>{it.email}</div>
                    <div>{it.phone}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLOR[it.status] || "bg-slate-100"}`}>{statusIconFor(it.status, 14)} {it.status}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{new Date(it.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={T.admin.open(it.application_id)} onClick={() => setSelected(it.application_id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1D4ED8] text-white text-xs font-semibold hover:bg-blue-800">
                      Open <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ApplicationDetail({ id, onBack }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [missingInfo, setMissingInfo] = useState("");
  const [saving, setSaving] = useState(false);
  const [docBlobs, setDocBlobs] = useState({});
  const [interviewAt, setInterviewAt] = useState("");
  const [interviewLocation, setInterviewLocation] = useState("");
  const [schedulingInterview, setSchedulingInterview] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.get(`/admin/applications/${id}`);
      setApp(data);
      setNewStatus(data.status);
      setInterviewAt(data.interview_at ? data.interview_at.slice(0, 16) : "");
      setInterviewLocation(data.interview_location || "");
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveStatus = async () => {
    if (!newStatus || newStatus === app.status) { toast.info("No status change"); return; }
    if (newStatus === "Additional Information Required" && !missingInfo.trim()) {
      toast.error("Describe what information is missing");
      return;
    }
    try {
      setSaving(true);
      await adminApi.put(`/admin/applications/${id}/status`, {
        status: newStatus,
        missing_info: newStatus === "Additional Information Required" ? missingInfo.trim() : undefined,
      });
      toast.success(newStatus === "Additional Information Required" ? "Applicant emailed with the missing info" : "Status updated");
      setMissingInfo("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const decide = async (status, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      setSaving(true);
      await adminApi.put(`/admin/applications/${id}/status`, { status });
      toast.success(status === "Approved" ? "Approved — applicant emailed" : "Rejected — applicant emailed");
      load();
    } catch { toast.error("Decision failed"); }
    finally { setSaving(false); }
  };

  const scheduleInterview = async () => {
    if (!interviewAt) { toast.error("Pick a date and time"); return; }
    try {
      setSchedulingInterview(true);
      const { data } = await adminApi.post(`/admin/applications/${id}/schedule-interview`, {
        interview_at: interviewAt,
        location: interviewLocation,
      });
      toast.success(`Interview scheduled — email sent · ${data.when_display}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule");
    } finally { setSchedulingInterview(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await adminApi.post(`/admin/applications/${id}/notes`, { note: note.trim() });
      setNote("");
      toast.success("Note added");
      load();
    } catch { toast.error("Failed to add note"); }
  };

  const viewDoc = async (path) => {
    if (docBlobs[path]) { window.open(docBlobs[path], "_blank"); return; }
    try {
      const res = await adminApi.get("/admin/files", { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setDocBlobs((b) => ({ ...b, [path]: url }));
      window.open(url, "_blank");
    } catch { toast.error("Cannot open document"); }
  };

  if (loading || !app) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-up">
      <button onClick={onBack} data-testid={T.admin.detailBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0A192F] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-1">Application</div>
          <h2 className="text-2xl font-bold text-[#0A192F] font-mono">{app.application_id}</h2>
          <div className="mt-1 text-slate-600">{app.first_name} {app.last_name} · {app.email} · {app.phone}</div>
        </div>
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLOR[app.status] || "bg-slate-100"}`}>{statusIconFor(app.status, 16)} {app.status}</span>
      </div>

      {/* Quick decision bar */}
      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-600 mr-auto">Quick decision:</div>
        <button
          data-testid="admin-approve-btn"
          onClick={() => decide("Approved", "Are you sure you want to APPROVE this applicant? They will receive a welcome email.")}
          disabled={saving || app.status === "Approved"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-semibold"
        >
          <ThumbsUp className="w-4 h-4" /> Approve
        </button>
        <button
          data-testid="admin-reject-btn"
          onClick={() => decide("Not Selected", "Reject this applicant? They will receive a polite rejection email.")}
          disabled={saving || app.status === "Not Selected"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold"
        >
          <ThumbsDown className="w-4 h-4" /> Reject & Email
        </button>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Personal">
            <Grid items={[
              ["City / State", `${app.city}, ${app.state} ${app.zip}`],
              ["Date of Birth", app.dob],
            ]} />
          </Section>
          <Section title="Driver">
            <Grid items={[
              ["License #", app.license_number], ["License State", app.license_state],
              ["Expiration", app.license_expiration], ["Years Experience", app.years_experience],
              ["Driving History", app.driving_history || "—"],
            ]} />
          </Section>
          <Section title="Vehicle">
            <Grid items={[
              ["Vehicle", `${app.vehicle_year} ${app.vehicle_make} ${app.vehicle_model}`],
            ]} />
          </Section>
          <Section title="Documents">
            {(!app.documents || app.documents.length === 0) && <div className="text-sm text-slate-500">No documents uploaded.</div>}
            <ul className="space-y-2">
              {app.documents?.map((d) => (
                <li key={d.storage_path} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0A192F] truncate">{d.label}</div>
                      <div className="text-xs text-slate-500 truncate">{d.original_filename}</div>
                    </div>
                  </div>
                  <button onClick={() => viewDoc(d.storage_path)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-semibold">View</button>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Agreements & Signature">
            <div className="text-sm text-slate-700">Waiver accepted: <strong>{app.waiver_accepted ? "Yes" : "No"}</strong></div>
            {app.signature_data && <img src={app.signature_data} alt="Signature" className="mt-3 border border-slate-200 rounded-lg max-h-40" />}
          </Section>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Update status</div>
            <select data-testid={T.admin.detailStatus} value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {newStatus === "Additional Information Required" && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-slate-700 mb-1">What information is missing?</label>
                <textarea
                  data-testid="admin-missing-info"
                  value={missingInfo}
                  onChange={(e) => setMissingInfo(e.target.value)}
                  rows={3}
                  placeholder="e.g. A clearer photo of your driver license"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">The applicant will see this exact text and receive it by email.</p>
              </div>
            )}
            {app.status === "Additional Information Required" && app.missing_info && (
              <div className="mt-3 rounded-xl bg-orange-50 border border-orange-100 p-3 text-xs text-orange-900">
                <span className="font-semibold">Currently requested:</span> {app.missing_info}
              </div>
            )}
            <button data-testid={T.admin.detailStatusSave} onClick={saveStatus} disabled={saving} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-semibold">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save status"}
            </button>
          </div>

          <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-5">
            <div className="text-xs uppercase tracking-wider text-indigo-700 font-semibold mb-2 flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Schedule Interview
            </div>
            <p className="text-xs text-slate-600 mb-3">Pick a date and time. This will set status to <strong>Interview</strong> and email the applicant.</p>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Date & Time</span>
              <input
                data-testid="admin-interview-datetime"
                type="datetime-local"
                value={interviewAt}
                onChange={(e) => setInterviewAt(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </label>
            <label className="block mt-3">
              <span className="text-xs font-semibold text-slate-700">Location (optional)</span>
              <input
                data-testid="admin-interview-location"
                value={interviewLocation}
                onChange={(e) => setInterviewLocation(e.target.value)}
                placeholder="Office address, video link, or 'To be confirmed'"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </label>
            <button
              onClick={scheduleInterview}
              disabled={schedulingInterview}
              data-testid="admin-schedule-interview"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold"
            >
              <Mail className="w-4 h-4" /> {schedulingInterview ? "Scheduling…" : "Schedule & Email"}
            </button>
            {app.interview_at && (
              <div className="mt-3 text-xs text-slate-600 bg-white rounded-lg border border-slate-200 p-2.5">
                <div className="font-semibold text-[#0A192F]">Currently scheduled:</div>
                <div>{new Date(app.interview_at).toLocaleString()}</div>
                {app.interview_location && <div className="text-slate-500 mt-0.5">{app.interview_location}</div>}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Internal notes</div>
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {(!app.notes || app.notes.length === 0) && <div className="text-sm text-slate-400">No notes yet.</div>}
              {app.notes?.map((n, i) => (
                <div key={i} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm">
                  <div className="text-slate-800">{n.text}</div>
                  <div className="mt-1 text-xs text-slate-500">{n.by} · {new Date(n.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <textarea data-testid={T.admin.detailNoteInput} value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a note…" className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" />
            <button data-testid={T.admin.detailNoteAdd} onClick={addNote} className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Add note
            </button>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">History</div>
            <ol className="border-l-2 border-slate-100 pl-4 space-y-3">
              {[...(app.history || [])].reverse().map((h, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#1D4ED8]" />
                  <div className="text-sm font-semibold text-[#0A192F]">{h.status}</div>
                  <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString()} · {h.by}</div>
                  {h.detail && <div className="text-xs text-slate-600 mt-0.5">{h.detail}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
function Grid({ items }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
          <dt className="text-slate-500">{k}</dt>
          <dd className="text-slate-800 font-medium text-right break-all">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
